import React, { useState } from 'react';
import { X, Link2, CheckCircle, AlertCircle } from 'lucide-react';
import { auth } from '../lib/firebase';
import {
  migrateWizardLocalStorage,
  readWizardLocalStorage,
  stripBlankFormData,
} from '../utils/wizardLocalStorage';

interface LinkJiraDialogProps {
  isOpen: boolean;
  pendingKey: string;
  customerName: string;
  appType: 'versirent' | 'idealone';
  onClose: () => void;
  onSuccess?: () => void;
}

export function LinkJiraDialog({
  isOpen,
  pendingKey,
  customerName,
  appType,
  onClose,
  onSuccess,
}: LinkJiraDialogProps) {
  const [jiraKey, setJiraKey] = useState('');
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [linkedKey, setLinkedKey] = useState<string | null>(null);

  const expectedPrefix = appType === 'versirent' ? 'VRD' : 'IAD';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        throw new Error('Admin session expired — sign in again to link Jira');
      }
      const localPending = readWizardLocalStorage(pendingKey);
      // IMA-20: only ship substantive fields — blanks/defaults must not clobber VRD shell
      const clientFormData = stripBlankFormData(localPending?.formData);
      const response = await fetch(
        'https://us-central1-ima-onboard.cloudfunctions.net/linkInvitedProject',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            pendingKey,
            jiraKey: jiraKey.trim(),
            sendWelcomeEmail,
            formData: clientFormData,
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to link Jira issue');
      }

      const newKey = data.data?.jiraKey || jiraKey.trim().toUpperCase();
      // IMA-20: remap wizard localStorage in this browser immediately
      migrateWizardLocalStorage(pendingKey, newKey);

      setSuccess(true);
      setLinkedKey(newKey);
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link Jira issue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setJiraKey('');
    setSendWelcomeEmail(false);
    setError(null);
    setSuccess(false);
    setLinkedKey(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Link2 className="h-5 w-5 text-blue-600" />
              Link Jira Issue
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {success ? (
            <div className="text-center py-6">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-900 font-medium">Linked successfully</p>
              <p className="text-sm text-gray-500 mt-1">
                {pendingKey} → {linkedKey}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-gray-600">
                Attach an existing Jira issue to{' '}
                <span className="font-medium text-gray-900">{customerName}</span>
                &apos;s pending invite ({pendingKey}). Use invite-first, then create the Jira
                ticket, then link here. Expected prefix:{' '}
                <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                  {expectedPrefix}-*
                </span>
                . If a portal shell already exists for that key, this will merge the invite into
                it.
              </p>

              <div>
                <label
                  htmlFor="jiraKey"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Jira Key
                </label>
                <input
                  id="jiraKey"
                  type="text"
                  value={jiraKey}
                  onChange={(e) => setJiraKey(e.target.value)}
                  placeholder={`${expectedPrefix}-123`}
                  required
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono uppercase"
                />
              </div>

              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={sendWelcomeEmail}
                  onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>
                  Send welcome email with the new portal link
                  <span className="block text-xs text-gray-500">
                    Off by default — the invite email was already sent. Enable only if they need
                    a remapped link.
                  </span>
                </span>
              </label>

              {error && (
                <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !jiraKey.trim()}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Linking…' : 'Link Jira'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
